import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  Upload as UploadIcon,
  CheckCircleOutlined as CheckCircleOutlineIcon,
  ErrorOutlined as ErrorOutlineIcon,
  WarningAmber as WarningAmberIcon,
  RemoveCircleOutlined as RemoveCircleOutlineIcon,
} from '@mui/icons-material';
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  CardActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Paper,
  Stack,
  Avatar,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import React, { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '../components/ui/PageLayout';
import { useSnackbar } from '../contexts/SnackbarContext';
import { DynamicJsonForm } from '../components/ui';

import { log } from '../utils/errors/logger';
import { getApiFetchUrl } from '../config/apiConfig';
import { getCsrfToken, requiresCsrfToken } from '../utils/auth/csrfManager';
import { getProviderIcon, getProviderColor } from '../utils/documents/documentSourceHelpers';
import { formatDate, formatDateTime, formatBytes } from '../utils/formatting/formatters';
import {
  useAdminProviderConfigs,
  useProviderConfigSyncStatus,
  documentSourceQueryKeys,
  useCreateProviderConfig,
  useUpdateProviderConfig,
  useDeleteProviderConfig,
  useSyncProviderConfig,
  useAvailableProviders,
} from '../hooks/api/useDocumentSources';
import type {
  DocumentSourceProviderConfigRequest,
  DocumentSourceProviderConfigResponse,
  ProviderConfigSyncStatus,
} from '../api/data-contracts';

const PFX_FIELD_KEYS = [
  'certificate_private_key',
  'certificate_thumbprint',
  'certificate_public_certificate',
] as const;

const schemaSupportsPfxUpload = (schema?: Record<string, unknown>): boolean => {
  const props = (schema?.properties ?? {}) as Record<string, unknown>;
  return PFX_FIELD_KEYS.every((key) => key in props);
};

interface PfxUploadPanelProps {
  onExtracted: (fields: {
    certificate_private_key: string;
    certificate_public_certificate: string;
    certificate_thumbprint: string;
  }) => void;
}

const PfxUploadPanel: React.FC<PfxUploadPanelProps> = ({ onExtracted }) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const [file, setFile] = useState<File | null>(null);
  const [passphrase, setPassphrase] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    if (!file) { return; }
    setUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      if (passphrase) {
        body.append('passphrase', passphrase);
      }
      const headers: Record<string, string> = {};
      if (requiresCsrfToken('POST')) {
        const csrfToken = getCsrfToken();
        if (csrfToken) {
          headers['X-CSRF-Token'] = csrfToken;
        }
      }
      const response = await fetch(`${getApiFetchUrl()}/document-sources/admin/parse-pfx`, {
        method: 'POST',
        headers,
        body,
        credentials: 'include',
      });
      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        throw new Error(detail?.detail || `HTTP ${response.status}`);
      }
      const data = await response.json() as {
        private_key_pem: string;
        public_certificate_pem: string;
        thumbprint: string;
      };
      onExtracted({
        certificate_private_key: data.private_key_pem,
        certificate_public_certificate: data.public_certificate_pem,
        certificate_thumbprint: data.thumbprint,
      });
      setPassphrase('');
      setFile(null);
      showSuccess(t('documentSourceConfig.pfxUpload.success'));
    } catch (error) {
      log.error('PFX parse failed', { error });
      const message = error instanceof Error ? error.message : '';
      showError(message || t('documentSourceConfig.pfxUpload.failed'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle1" gutterBottom>
        {t('documentSourceConfig.pfxUpload.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('documentSourceConfig.pfxUpload.description')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button
          component="label"
          variant="outlined"
          startIcon={<UploadIcon />}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('documentSourceConfig.pfxUpload.selectFile')}
          <input
            type="file"
            hidden
            accept=".pfx,.p12,application/x-pkcs12"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </Button>
        {file && (
          <Typography variant="caption" color="text.secondary">
            {t('documentSourceConfig.pfxUpload.selectedFile', { name: file.name })}
          </Typography>
        )}
        <TextField
          label={t('documentSourceConfig.pfxUpload.passphraseLabel')}
          type="password"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          size="small"
          autoComplete="off"
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleUpload}
          disabled={!file || uploading}
          sx={{ alignSelf: 'flex-start' }}
        >
          {uploading
            ? <CircularProgress size={20} sx={{ color: 'inherit' }} />
            : t('documentSourceConfig.pfxUpload.uploadButton')}
        </Button>
      </Box>
    </Box>
  );
};

type AvailableProviderMeta = {
  provider_key: string;
  display_name?: string;
  name?: string;
  capabilities?: {
    external_acl_mode?: string;
    [k: string]: unknown;
  };
  schemas?: {
    admin_config_schema?: Record<string, unknown>;
    user_config_schema?: Record<string, unknown>;
    ui_schema?: Record<string, unknown>;
  };
};

/**
 * The synchronisation line on a provider card.
 *
 * Reads the stored state of every connection under the config, so the card
 * says when the source was last synchronised and how many documents came in
 * even when the page was just opened. While a run is on its way the query
 * repeats itself, so the counts appear without a reload.
 */
const SyncStatusLine: React.FC<{ configId: string; live: boolean }> = ({ configId, live }) => {
  const { t, i18n } = useTranslation();
  const { data } = useProviderConfigSyncStatus(configId, live);
  const connections = data?.connections ?? [];

  // Totals the server already summed across the connections of this source.
  const documentCount = data?.document_count ?? 0;
  const totalBytes = data?.total_size_bytes ?? 0;
  const skippedFiles = data?.skipped_files ?? 0;
  const unchangedFiles = data?.unchanged_files ?? 0;

  // Whether a run is working is the server's knowledge, not this tab's: it
  // holds after a reload and is the same for every administrator. ``live``
  // only bridges the seconds between the click and the worker's first mark.
  const runningConnection = connections.find(c => c.sync_state === 'running');
  const stalledConnection = connections.find(c => c.sync_state === 'stalled');
  // Enqueued, but the worker is still busy with something else.
  const queued = !runningConnection && connections.some(c => c.sync_state === 'queued');
  const running = live || queued || Boolean(runningConnection);
  const processed = connections.reduce((sum, c) => sum + Number(c.sync_progress?.processed ?? 0), 0);
  const liveCount = (key: string) =>
    connections.reduce((sum, c) => sum + Number(c.sync_progress?.[key] ?? 0), 0);
  // The size of the source as the last full walk counted it: good enough to say
  // "46 of 74", not shown when the run has already passed it.
  const knownTotal = connections.reduce((sum, c) => sum + Number(c.external_ingest_stats?.total ?? 0), 0);
  const successDates = connections
    .map(c => c.last_successful_sync_at)
    .filter((d): d is string => Boolean(d))
    .sort();
  const lastSuccess = successDates.length > 0 ? successDates[successDates.length - 1] : undefined;
  const interval = connections.find(c => c.sync_interval)?.sync_interval ?? undefined;
  const retries = connections.reduce((max, c) => Math.max(max, Number(c.sync_retry_count ?? 0)), 0);

  const connectionError = connections.find(c => c.status === 'error' || c.last_error);
  // Reasons the server names by a stable key get a sentence an administrator
  // can act on; anything else is shown as it came.
  const errorText = connectionError?.last_error
    ? t(`documentSourceConfig.sync.reasons.${connectionError.last_error}`, {
        defaultValue: connectionError.last_error,
      })
    : undefined;
  const total = connections.reduce((sum, c) => sum + Number(c.external_ingest_stats?.total ?? 0), 0);
  // A file that is provably what is indexed already counts as fine. Counting
  // only what was written made a run over an unchanged source read as "none of
  // the 74 files could be ingested", in red.
  const indexed = connections.reduce(
    (sum, c) => sum + Number(c.external_ingest_stats?.successful ?? 0)
      + Number(c.external_ingest_stats?.unchanged ?? 0),
    0
  );
  const failedFiles = connections.reduce((sum, c) => sum + Number(c.external_ingest_stats?.failed ?? 0), 0);
  // Files the source no longer lists: dropped from the index after a full walk,
  // or kept when the walk looked broken (empty, or more than half gone).
  const removedFiles = connections.reduce((sum, c) => sum + Number(c.external_ingest_stats?.removed ?? 0), 0);
  const heldFiles = connections.reduce(
    (sum, c) => sum + (c.external_ingest_stats?.removal_held ? Number(c.external_ingest_stats?.gone ?? 0) : 0),
    0
  );
  // Which site and folder this source actually syncs. All connections under
  // one config point at the same source, so the first one that names a target
  // stands for the card.
  const target = connections.find(c => c.source_label || c.source_detail);
  const runDates = connections
    .map(c => c.last_sync_at)
    .filter((d): d is string => Boolean(d))
    .sort();
  const lastRun = runDates.length > 0 ? runDates[runDates.length - 1] : undefined;

  // One verdict for the whole source, so the card cannot say "5 files found"
  // in green while every one of them failed.
  //   error   the run never reached the source, or no file survived it
  //   warning the source is empty, or some files failed
  //   success everything that was found was indexed
  const severity: 'error' | 'warning' | 'success' | 'idle' = (() => {
    if (connectionError) { return 'error'; }
    if (!lastRun) { return 'idle'; }
    if (total === 0) { return 'warning'; }
    if (indexed === 0) { return 'error'; }
    return failedFiles > 0 ? 'warning' : 'success';
  })();

  const headline = (() => {
    switch (severity) {
      case 'error':
        return connectionError
          ? t('documentSourceConfig.sync.failed')
          : t('documentSourceConfig.sync.noneIndexed', { total });
      case 'warning':
        return total === 0
          ? t('documentSourceConfig.sync.empty')
          : t('documentSourceConfig.sync.partial', { indexed, total });
      case 'success':
        return t('documentSourceConfig.sync.done', { total, indexed });
      default:
        return t('documentSourceConfig.sync.never');
    }
  })();

  const icon = {
    error: <ErrorOutlineIcon fontSize="inherit" color="error" />,
    warning: <WarningAmberIcon fontSize="inherit" color="warning" />,
    success: <CheckCircleOutlineIcon fontSize="inherit" color="success" />,
    idle: <RemoveCircleOutlineIcon fontSize="inherit" color="disabled" />,
  }[severity];

  /** One detail line: label left, value right, so the rows line up. */
  const Row: React.FC<{ label: string; value: string; warn?: boolean }> = ({ label, value, warn }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="caption" color={warn ? 'warning.main' : 'text.primary'} sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 1, bgcolor: 'action.hover' }}>
      {/* Verdict of the last run, with the reason when there is one */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
        {running
          ? <CircularProgress size={14} />
          : stalledConnection ? <WarningAmberIcon fontSize="inherit" color="warning" /> : icon}
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {running
            ? (processed > 0
              ? (knownTotal >= processed && knownTotal > 0
                ? t('documentSourceConfig.sync.runningProgressOf', { processed, total: knownTotal })
                : t('documentSourceConfig.sync.runningProgress', { processed }))
              : queued ? t('documentSourceConfig.sync.queued') : t('documentSourceConfig.sync.running'))
            : stalledConnection ? t('documentSourceConfig.sync.stalled') : headline}
        </Typography>
      </Box>

      {!running && stalledConnection && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 0.75 }}>
          {t('documentSourceConfig.sync.stalledHint', {
            since: formatDateTime(stalledConnection.sync_heartbeat_at, i18n.language),
          })}
        </Typography>
      )}

      {!running && !stalledConnection && errorText && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 0.75 }}>
          {errorText}
        </Typography>
      )}

      {/* What the source holds and what the last run did with it */}
      <Stack spacing={0.25}>
        {target?.source_label && (
          <Row
            label={t('documentSourceConfig.sync.sourceLabel')}
            value={target.source_label}
          />
        )}
        {target?.source_detail && (
          <Row
            label={t('documentSourceConfig.sync.sourceDetail')}
            value={target.source_detail}
          />
        )}
        {documentCount > 0 && (
          <Row
            label={t('documentSourceConfig.sync.documents')}
            value={totalBytes > 0
              ? `${documentCount} (${formatBytes(totalBytes)})`
              : String(documentCount)}
          />
        )}
        {/* While a run works, its own counters; afterwards those of the last run. */}
        {runningConnection ? (
          <>
            {liveCount('successful') > 0 && (
              <Row label={t('documentSourceConfig.sync.ingestedLabel')} value={String(liveCount('successful'))} />
            )}
            {liveCount('unchanged') > 0 && (
              <Row label={t('documentSourceConfig.sync.unchangedLabel')} value={String(liveCount('unchanged'))} />
            )}
            {liveCount('skipped') > 0 && (
              <Row label={t('documentSourceConfig.sync.skippedLabel')} value={String(liveCount('skipped'))} warn />
            )}
            {liveCount('failed') > 0 && (
              <Row label={t('documentSourceConfig.sync.failedLabel')} value={String(liveCount('failed'))} warn />
            )}
          </>
        ) : (
          <>
            {unchangedFiles > 0 && (
              <Row label={t('documentSourceConfig.sync.unchangedLabel')} value={String(unchangedFiles)} />
            )}
            {failedFiles > 0 && (
              <Row label={t('documentSourceConfig.sync.failedLabel')} value={String(failedFiles)} warn />
            )}
            {skippedFiles > 0 && (
              <Row label={t('documentSourceConfig.sync.skippedLabel')} value={String(skippedFiles)} warn />
            )}
            {removedFiles > 0 && (
              <Row label={t('documentSourceConfig.sync.removedLabel')} value={String(removedFiles)} />
            )}
            {heldFiles > 0 && (
              <Row label={t('documentSourceConfig.sync.heldLabel')} value={String(heldFiles)} warn />
            )}
          </>
        )}
        {runningConnection?.sync_started_at && (
          <Row
            label={t('documentSourceConfig.sync.runningSince')}
            value={formatDateTime(runningConnection.sync_started_at, i18n.language)}
          />
        )}
        {runningConnection?.sync_heartbeat_at && (
          <Row
            label={t('documentSourceConfig.sync.heartbeat')}
            value={formatDateTime(runningConnection.sync_heartbeat_at, i18n.language)}
          />
        )}
        {lastRun && (
          <Row
            // While a run works, the date shown belongs to the run BEFORE it.
            label={t(running ? 'documentSourceConfig.sync.previousRun' : 'documentSourceConfig.sync.lastRun')}
            value={formatDateTime(lastRun, i18n.language)}
          />
        )}
        {/* Only when it says something the row above does not: the last run failed. */}
        {lastSuccess && lastSuccess !== lastRun && (
          <Row
            label={t('documentSourceConfig.sync.lastSuccess')}
            value={formatDateTime(lastSuccess, i18n.language)}
          />
        )}
        {!lastSuccess && lastRun && !running && (
          <Row label={t('documentSourceConfig.sync.lastSuccess')} value={t('documentSourceConfig.sync.neverSucceeded')} warn />
        )}
        {interval && (
          <Row
            label={t('documentSourceConfig.sync.automatic')}
            value={t(`documentSourceConfig.sync.intervals.${interval}`, { defaultValue: interval })}
          />
        )}
        {retries > 0 && !running && (
          <Row
            label={t('documentSourceConfig.sync.retries')}
            value={t('documentSourceConfig.sync.retriesValue', { count: retries })}
            warn
          />
        )}
      </Stack>
    </Box>
  );
};

/**
 * The "synchronise now" button of a provider card. Locked while the server
 * reports a run on this source, whoever started it: a second click would only
 * be answered with "already running".
 */
const SyncButton: React.FC<{
  configId: string;
  enabled: boolean;
  pending: boolean;
  onClick: () => void;
}> = ({ configId, enabled, pending, onClick }) => {
  const { t } = useTranslation();
  const { data } = useProviderConfigSyncStatus(configId);
  const running = (data?.connections ?? []).some(
    c => c.sync_state === 'running' || c.sync_state === 'queued'
  );
  const label = running
    ? t('documentSourceConfig.sync.alreadyRunning')
    : t('documentSourceConfig.syncNow');
  return (
    // The span keeps the tooltip title reachable while the button is disabled.
    <span title={label}>
      <IconButton
        size="small"
        onClick={onClick}
        color="primary"
        disabled={pending || running || !enabled}
        aria-label={label}
      >
        {pending ? <CircularProgress size={20} /> : <SyncIcon />}
      </IconButton>
    </span>
  );
};

const DocumentSourceConfig: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { showSuccess, showError, showWarning, showInfo } = useSnackbar();
  const queryClient = useQueryClient();
  const syncPollRef = useRef<number | null>(null);

  /**
   * What the card shows about the last or the running synchronisation.
   * ``running`` while the job is on its way, then the outcome with the
   * numbers so the administrator sees on the card whether files arrived.
   */
  type SyncCardState = {
    phase: 'running' | 'done' | 'empty' | 'error';
    total?: number;
    indexed?: number;
    reason?: string;
    at?: string;
  };
  const [syncState, setSyncState] = useState<Record<string, SyncCardState>>({});
  
  // Queries
  const { data: configs = [], isLoading: configsLoading } = useAdminProviderConfigs();
  const { data: availableProvidersRaw = [], isLoading: providersLoading } = useAvailableProviders();
  const availableProviders = availableProvidersRaw as unknown as AvailableProviderMeta[];
  
  // Mutations
  const createProviderConfig = useCreateProviderConfig();
  const updateProviderConfig = useUpdateProviderConfig();
  const deleteProviderConfig = useDeleteProviderConfig();
  const syncProviderConfig = useSyncProviderConfig();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<DocumentSourceProviderConfigResponse | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<DocumentSourceProviderConfigResponse | null>(null);

  // Form state
  const [formData, setFormData] = useState<DocumentSourceProviderConfigRequest>({
    provider_type: '',
    provider_key: '',
    name: '',
    description: null,
    enabled: true,
    provider_config: {},
    initial_connection: null,
  });

  // Selected provider metadata for schema
  const selectedProviderMeta = availableProviders.find(p => p.provider_key === formData.provider_key);
  const selectedExternalAclMode = selectedProviderMeta?.capabilities?.external_acl_mode;
  const selectedSupportsExternalAcl =
    Boolean(selectedExternalAclMode) && selectedExternalAclMode !== 'none';

  const handleOpenDialog = (config?: DocumentSourceProviderConfigResponse) => {
    if (config) {
      setEditingConfig(config);
      setFormData({
        provider_type: config.provider_type,
        provider_key: config.provider_key,
        name: config.name,
        description: config.description ?? null,
        enabled: config.enabled,
        // Stored values come back without secrets; secret fields show as "stored"
        provider_config: { ...(config.provider_config ?? {}) },
        initial_connection: null, // Initial connection is only relevant on creation
      });
    } else {
      setEditingConfig(null);
      // Default to first available provider if any
      const firstProvider = availableProviders[0]?.provider_key || '';

      setFormData({
        provider_type: firstProvider,
        provider_key: firstProvider,
        name: '',
        description: null,
        enabled: true,
        provider_config: {},
        initial_connection: null,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingConfig(null);
  };

  const handleSaveConfig = async () => {
    try {
      if (editingConfig) {
        await updateProviderConfig.mutateAsync({
          configId: editingConfig.id,
          data: formData,
        });
      } else {
        await createProviderConfig.mutateAsync(formData);
      }
      if (selectedSupportsExternalAcl && formData.initial_connection) {
        showSuccess(t('documentSourceConfig.messages.saveAndSyncStarted'));
      } else {
        showSuccess(t('documentSourceConfig.messages.saveSuccess'));
      }

      handleCloseDialog();
    } catch (error) {
      log.error('Error saving provider config', { error });
      showError(t('documentSourceConfig.messages.saveFailed'));
    }
  };

  /**
   * Watch the connections of a config after a sync was enqueued and report
   * the outcome: red with the reason when the run failed, yellow when the
   * source turned out to be empty, green with the counts when files arrived.
   *
   * The card itself reads the stored state through useProviderConfigSyncStatus
   * and refreshes on its own while a run is live; this watcher only produces
   * the message and ends the "running" state.
   */
  const watchSyncOutcome = (configId: string, lastRunBefore: string) => {
    const POLL_MS = 3000;
    // How long to wait for the worker to pick the job up. Once the server
    // reports the run, there is no limit: the watcher follows it to its end.
    const MAX_POLLS_UNTIL_PICKED_UP = 40;
    let polls = 0;
    let sawRunning = false;
    let stillQueued = false;
    if (syncPollRef.current) { window.clearTimeout(syncPollRef.current); }
    const tick = async () => {
      polls += 1;
      try {
        const { data } = await apiClient.adminGetProviderConfigSyncStatus(configId);
        const connections = data.connections ?? [];
        // Finished is what the SERVER says is finished: no run working any
        // more, and a run date newer than the one before the click. The old
        // comparison against this browser's clock never came true on a
        // machine whose clock ran ahead of the server's.
        const running = connections.some(c => c.sync_state === 'running');
        // Still waiting for a free worker: neither finished nor lost.
        const waiting = connections.some(c => c.sync_state === 'queued');
        sawRunning = sawRunning || running;
        stillQueued = waiting;
        const finished = running || waiting
          ? []
          : connections.filter(c => c.last_sync_at && (sawRunning || c.last_sync_at > lastRunBefore));
        const failed = finished.find(c => c.status === 'error' || c.last_error);
        if (failed) {
          const reason = failed.last_error ?? failed.status;
          setSyncState(prev => ({ ...prev, [configId]: { phase: 'error', reason, at: failed.last_sync_at ?? undefined } }));
          showError(t('documentSourceConfig.messages.syncError', { reason }));
          void queryClient.invalidateQueries({ queryKey: ['document-sources'] });
          return;
        }
        if (connections.length > 0 && finished.length === connections.length) {
          const total = finished.reduce((sum, c) => sum + Number(c.external_ingest_stats?.total ?? 0), 0);
          const indexed = finished.reduce((sum, c) => sum + Number(c.external_ingest_stats?.successful ?? 0), 0);
          const at = finished[0]?.last_sync_at ?? undefined;
          if (total === 0) {
            setSyncState(prev => ({ ...prev, [configId]: { phase: 'empty', total, indexed, at } }));
            showWarning(t('documentSourceConfig.messages.syncNoFiles'));
          } else {
            setSyncState(prev => ({ ...prev, [configId]: { phase: 'done', total, indexed, at } }));
            showSuccess(t('documentSourceConfig.messages.syncDone', { total, indexed }));
          }
          void queryClient.invalidateQueries({ queryKey: ['document-sources'] });
          return;
        }
      } catch (error) {
        log.warn('Sync status poll failed', { error });
      }
      if (sawRunning || stillQueued || polls < MAX_POLLS_UNTIL_PICKED_UP) {
        syncPollRef.current = window.setTimeout(() => { void tick(); }, POLL_MS);
      } else {
        // Nobody picked the job up. The card falls back to what the server
        // knows instead of spinning on this tab's word alone.
        setSyncState(prev => {
          const { [configId]: _dropped, ...rest } = prev;
          return rest;
        });
        showInfo(t('documentSourceConfig.messages.syncStillRunning'));
      }
    };
    syncPollRef.current = window.setTimeout(() => { void tick(); }, POLL_MS);
  };

  const handleSyncConfig = async (config: DocumentSourceProviderConfigResponse) => {
    try {
      const before = queryClient.getQueryData<ProviderConfigSyncStatus>(
        documentSourceQueryKeys.adminProviderConfigSyncStatus(config.id)
      );
      const lastRunBefore = (before?.connections ?? [])
        .map(c => c.last_sync_at ?? '')
        .sort()
        .pop() ?? '';
      setSyncState(prev => ({ ...prev, [config.id]: { phase: 'running' } }));
      const result = await syncProviderConfig.mutateAsync(config.id);
      showInfo(
        t('documentSourceConfig.messages.syncEnqueued', {
          count: result.connection_count,
        })
      );
      watchSyncOutcome(config.id, lastRunBefore);
    } catch (error: any) {
      log.error('Error triggering provider config sync', { error });
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      setSyncState(prev => ({ ...prev, [config.id]: { phase: 'error', reason: detail } }));
      showError(detail || t('documentSourceConfig.messages.syncFailed'));
    }
  };

  const handleDeleteConfig = async () => {
    if (!configToDelete) { return; }

    try {
      await deleteProviderConfig.mutateAsync(configToDelete.id);
      showSuccess(t('documentSourceConfig.messages.deleteSuccess'));
      setDeleteConfirmOpen(false);
      setConfigToDelete(null);
    } catch (error: any) {
      log.error('Error deleting provider config', { error });

      if (error?.response?.status === 400 && error?.response?.data?.error_code === 'ACTIVE_CONNECTIONS_EXIST') {
        const activeCount = error?.response?.data?.active_connections || 0;
        const errorMsg = t('documentSourceConfig.messages.deleteFailedActiveConnections', { count: activeCount });
        showError(errorMsg);
      } else if (error?.response?.data?.message) {
        showError(error.response.data.message);
      } else {
        showError(t('documentSourceConfig.messages.deleteFailed'));
      }

      setDeleteConfirmOpen(false);
      setConfigToDelete(null);
    }
  };

  const formatProviderType = (config: DocumentSourceProviderConfigResponse): string => {
    const meta = availableProviders.find(p => p.provider_key === config.provider_key);
    if (meta) {
      return meta.display_name || meta.name || config.provider_key;
    }
    return config.provider_key;
  };

  const isLoading = configsLoading || providersLoading;

  if (isLoading && configs.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <PageLayout
        title={t('documentSourceConfig.title')}
        headerActions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            disabled={availableProviders.length === 0}
          >
            {t('documentSourceConfig.addProvider')}
          </Button>
        }
        isLoading={isLoading}
        maxWidth="xl"
      >
        {configs.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body1" color="text.secondary">
              {t('documentSourceConfig.noProviders')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {configs.map((config) => (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={config.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: getProviderColor(config.provider_key), mr: 2 }}>
                        {getProviderIcon(config.provider_key)}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6">{config.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatProviderType(config)}
                        </Typography>
                      </Box>
                      <Chip
                        label={config.enabled ? t('common.enabled') : t('common.disabled')}
                        color={config.enabled ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>

                    {config.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {config.description}
                      </Typography>
                    )}

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {t('common.createdAt')}: {formatDate(config.created_at, i18n.language)}
                    </Typography>

                    <SyncStatusLine configId={config.id} live={syncState[config.id]?.phase === 'running'} />
                  </CardContent>
                  <CardActions>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(config)}
                      color="primary"
                      title={t('documentSourceConfig.editProvider')}
                      aria-label={t('documentSourceConfig.editProvider')}
                    >
                      <EditIcon />
                    </IconButton>
                    {(() => {
                      const meta = availableProviders.find(p => p.provider_key === config.provider_key);
                      const aclMode = meta?.capabilities?.external_acl_mode;
                      const supportsSync = Boolean(aclMode) && aclMode !== 'none';
                      if (!supportsSync) { return null; }
                      const isSyncing = syncProviderConfig.isPending &&
                        syncProviderConfig.variables === config.id;
                      return (
                        <SyncButton
                          configId={config.id}
                          enabled={Boolean(config.enabled)}
                          pending={isSyncing}
                          onClick={() => { void handleSyncConfig(config); }}
                        />
                      );
                    })()}
                    <IconButton
                      size="small"
                      onClick={() => {
                        setConfigToDelete(config);
                        setDeleteConfirmOpen(true);
                      }}
                      color="error"
                      title={t('documentSourceConfig.deleteProvider')}
                      aria-label={t('documentSourceConfig.deleteProvider')}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </PageLayout>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingConfig
            ? t('documentSourceConfig.editProvider')
            : t('documentSourceConfig.addProvider')}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2.5} sx={{ pt: 1.5 }}>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t('documentSourceConfig.sections.general')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('documentSourceConfig.sections.generalHint')}</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }} key="provider-key">
                  <FormControl fullWidth>
                    <InputLabel id="provider-type-label">{t('documentSourceConfig.providerType')}</InputLabel>
                    <Select
                      labelId="provider-type-label"
                      label={t('documentSourceConfig.providerType')}
                      value={formData.provider_key}
                      onChange={(e) => {
                        const key = e.target.value as string;
                        setFormData({
                          ...formData,
                          provider_key: key,
                          provider_type: key
                        });
                      }}
                      disabled={Boolean(editingConfig)}
                    >
                      {availableProviders.map((provider) => (
                        <MenuItem key={provider.provider_key} value={provider.provider_key}>
                          {t(`documentSources.providerNames.${provider.provider_key}`, { defaultValue: provider.display_name || provider.name || provider.provider_key })}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }} key="name">
                  <TextField
                    fullWidth
                    label={t('common.name')}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    helperText={t('documentSourceConfig.nameHelp')}
                  />
                </Grid>
                <Grid size={{ xs: 12 }} key="description">
                  <TextField
                    fullWidth
                    label={t('common.description')}
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    multiline
                    rows={2}
                    helperText={t('documentSourceConfig.descriptionHelp')}
                  />
                </Grid>
                <Grid size={{ xs: 12 }} key="enabled">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enabled}
                        onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1">{t('common.enabled')}</Typography>
                        <Typography variant="caption" color="text.secondary">{t('documentSourceConfig.enabledHelp')}</Typography>
                      </Box>
                    }
                  />
                </Grid>
              </Grid>
            </Paper>

            {selectedProviderMeta?.schemas?.admin_config_schema && (
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }} key="config-section">
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t('documentSourceConfig.sections.credentials')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('documentSourceConfig.sections.credentialsHint')}</Typography>
                <DynamicJsonForm
                  schema={selectedProviderMeta.schemas.admin_config_schema}
                  value={formData.provider_config || {}}
                  onChange={(config) => setFormData({ ...formData, provider_config: config })}
                  secretsSet={editingConfig?.secrets_set ?? []}
                  storedSecretHint={t('documentSourceConfig.secretStored')}
                  i18nPrefix={`documentSources.schema.${formData.provider_key}`}
                />
                {schemaSupportsPfxUpload(selectedProviderMeta.schemas.admin_config_schema) && (
                  <PfxUploadPanel
                    onExtracted={(fields) => setFormData((prev) => ({
                      ...prev,
                      provider_config: { ...(prev.provider_config || {}), ...fields },
                    }))}
                  />
                )}
              </Paper>
            )}

            {selectedSupportsExternalAcl &&
              selectedProviderMeta?.schemas?.user_config_schema && (
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }} key="initial-connection">
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t('documentSourceConfig.initialConnectionTitle')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('documentSourceConfig.initialConnectionHint')}</Typography>
                <DynamicJsonForm
                  schema={selectedProviderMeta.schemas.user_config_schema}
                  value={(formData.initial_connection || {}) as Record<string, unknown>}
                  onChange={(value) => setFormData({
                    ...formData,
                    initial_connection: value && Object.keys(value).length > 0 ? value : null,
                  })}
                  i18nPrefix={`documentSources.schema.${formData.provider_key}`}
                />
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSaveConfig} variant="contained" color="primary">
            {editingConfig ? t('common.update') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{t('common.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('documentSourceConfig.confirmDeleteProvider', {
              name: configToDelete?.name || ''
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteConfig} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentSourceConfig;
